import { notification } from 'antd';
import {
    createContext,
    FC,
    useEffect,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { getDispersion, getTransactions } from '../../../api/dispersions';
import { IDispersion, IDispersionTransaction } from '../../../interfaces/dispersion.interface';

interface IDispersionContext {
    loading: boolean,
    loadingTransactions: boolean,
    dispersion: IDispersion,
    transactions: IDispersionTransaction[],
    total: number,
    paginator: {
        limit: number,
        page: number,
    },
    onPageChange: (page: number, pageSize: number) => void,
    fetchDispersion: () => void,
}

const initialContext = {
    loading: false,
    loadingTransactions: false,
    dispersion: {} as IDispersion,
    transactions: [] as IDispersionTransaction[],
    total: 0,
    paginator: {
        limit: 10,
        page: 1,
    },
    onPageChange: () => { },
    fetchDispersion: () => { },
};

export const DispersionContext = createContext<IDispersionContext>(initialContext);

export const DispersionProvider: FC = ({ children }) => {
    const [loading, setLoading] = useState<boolean>(initialContext.loading);
    const [loadingTransactions, setLoadingTransactions] = useState<boolean>(initialContext.loadingTransactions);
    const [dispersion, setDispersion] = useState<IDispersion>(initialContext.dispersion);
    const [transactions, setTransactions] = useState<IDispersionTransaction[]>([]);
    const [total, setTotal] = useState<number>(initialContext.total);
    const [paginator, setPaginator] = useState(initialContext.paginator);

    const { id } = useParams();

    const dispersionID = id;

    const onPageChange = (page: number, pageSize: number) => {
        setPaginator({
            limit: pageSize,
            page,
        });
    };

    const fetchTransactions = () => {
        setLoadingTransactions(true);

        const params: any = {
            ...paginator,
        };

        getTransactions(dispersionID as string, params)
            .then((response: any) => {
                const { data: { data } } = response;
                setTransactions(data.data);
                setTotal(data.total);
            })
            .catch((error: any) => {
                console.log({ error });
                const description = error.response.data.description;
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => setLoadingTransactions(false));
    };

    const fetchDispersion = () => {
        setLoading(true);
        getDispersion(dispersionID as string)
            .then((response: any) => {
                const { data } = response.data;
                setDispersion((prev) => ({
                    ...prev,
                    ...data,
                }));
                if (data?._id) {
                    fetchTransactions();
                }
            })
            .catch((error: any) => {
                console.log({ error });
                const description = error.response.data.description;
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTransactions();
    }, [paginator]);

    return (
        <DispersionContext.Provider
            value={{
                loading,
                loadingTransactions,
                dispersion,
                total,
                paginator,
                onPageChange,
                fetchDispersion,
                transactions,
            }}
        >
            {children}
        </DispersionContext.Provider>
    );
};
